import { useEffect, useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';

export interface ReferenceImageViewerImage {
  id: string;
  url: string;
  thumbnailUrl?: string | null;
  fileName: string;
}

interface ReferenceImageViewerModalProps {
  image: ReferenceImageViewerImage | null;
  onClose: () => void;
}

async function downloadImage(url: string, fileName: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName || 'image';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Cross-origin or network fallback: open in a new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function ReferenceImageViewerModal({
  image,
  onClose,
}: ReferenceImageViewerModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!image) return;
    setLoading(true);
    setError(false);
  }, [image?.id, image?.url]);

  useEffect(() => {
    if (!image) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [image, onClose]);

  if (!image) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadImage(image.url, image.fileName);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={image.fileName}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <p className="truncate text-sm font-medium text-gray-900" title={image.fileName}>
            {image.fileName}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={downloading || error}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Télécharger
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-1.5 text-gray-700 hover:bg-gray-50"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="relative flex min-h-[240px] flex-1 items-center justify-center bg-gray-950 p-4">
          {loading && !error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-gray-950/80 text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm text-white/80">Chargement de l&apos;image…</p>
            </div>
          )}

          {error ? (
            <p className="text-sm text-red-300">Impossible de charger l&apos;image.</p>
          ) : (
            <img
              src={image.url}
              alt={image.fileName}
              className={`max-h-[75vh] max-w-full object-contain transition-opacity ${
                loading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError(true);
              }}
            />
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
